import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import {
  createTeam,
  deleteTeam,
  getUserTeam,
  getUserTeamInvitations,
  inviteTeamMembers,
  leaveTeam,
  manageTeamInvitation,
  removeTeamMember,
  transferTeamOwnership,
  updateTeam,
} from '@/services/core.service'
import type {
  IInviteTeamMembersForm,
  IRemoveTeamMemberForm,
  ITeamForm,
  ITransferTeamOwnershipForm,
} from '@/types/core/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export const useTeams = () => {
  const client = useQueryClient()

  const getUserTeamQuery = () =>
    useQuery({
      queryKey: ['user-team'],
      queryFn: () => getUserTeam(),
    })

  const getUserTeamInvitationsQuery = () =>
    useQuery({
      queryKey: ['user-team-invitations'],
      queryFn: () => getUserTeamInvitations(),
    })

  const createTeamMutation = useMutation({
    mutationFn: (form: ITeamForm) => createTeam(form),
    onError: NotifyErrorWrapper('Failed to create team'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['user-team'] })
      NotifySuccess(`Team created successfully`)
    },
  })

  const updateTeamMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: any }) => updateTeam(id, form),
    onError: NotifyErrorWrapper('Failed to update team'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['user-team'] })
      NotifySuccess(`Team updated`)
    },
  })

  const deleteTeamMutation = useMutation({
    mutationFn: (id: number) => deleteTeam(id),
    onError: NotifyErrorWrapper('Failed to delete team'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['user-team'] })
      NotifySuccess(`Team deleted successfully`)
    },
  })

  const inviteTeamMembersMutation = useMutation({
    mutationFn: ({ id, form }: { id: number; form: IInviteTeamMembersForm }) => inviteTeamMembers(id, form),
    onError: NotifyErrorWrapper('Failed to invite members'),
    onSuccess: () => {
      NotifySuccess(`Invitations sent`)
    },
  })

  const removeTeamMemberMutation = useMutation({
    mutationFn: (form: IRemoveTeamMemberForm) => removeTeamMember(form.teamID, form.memberID),
    onError: NotifyErrorWrapper('Failed to delete team member'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['user-team'] })
      NotifySuccess(`Team member deleted`)
    },
  })

  const transferTeamOwnershipMutation = useMutation({
    mutationFn: (form: ITransferTeamOwnershipForm) => transferTeamOwnership(form.teamID, form.memberID),
    onError: NotifyErrorWrapper('Failed to transfer team ownership'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['user-team'] })
      NotifySuccess(`Team ownership transfered successfully`)
    },
  })

  const leaveTeamMutation = useMutation({
    mutationFn: () => leaveTeam(),
    onError: NotifyErrorWrapper('Failed to leave team'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['user-team'] })
      NotifySuccess(`Team left`)
    },
  })

  const manageTeamInvitationMutation = useMutation({
    mutationFn: ({ invitationID, accept }: { invitationID: number; accept: boolean }) =>
      manageTeamInvitation(invitationID, accept),
    onError: NotifyErrorWrapper('Failed to manage team invitation'),
    onSuccess: (_data, variables) => {
      client.invalidateQueries({ queryKey: ['user-team-invitations'] })
      NotifySuccess(`Invitation ${variables.accept ? 'accepted' : 'rejected'}`)
    },
  })

  return {
    getUserTeamQuery,
    getUserTeamInvitationsQuery,
    createTeamMutation,
    updateTeamMutation,
    deleteTeamMutation,
    leaveTeamMutation,
    removeTeamMemberMutation,
    inviteTeamMembersMutation,
    transferTeamOwnershipMutation,
    manageTeamInvitationMutation,
  }
}
